import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getWorkOrders, getMyWorkOrders, downloadWorkOrdersCsv } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Search, 
  Plus, 
  Filter, 
  MapPin, 
  Calendar,
  ChevronRight,
  RefreshCw,
  ClipboardList,
  Download
} from 'lucide-react';
import { toast } from 'sonner';

const WorkOrdersPage = () => {
  const navigate = useNavigate();
  const { user, isSupervisor } = useAuth();
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [projectTypeFilter, setProjectTypeFilter] = useState('all');

  // Check if user is a crew_leader (not supervisor/admin)
  const isCrewLeaderOnly = user?.role === 'crew_leader';

  useEffect(() => {
    fetchWorkOrders();
  }, [statusFilter, projectTypeFilter]);

  const fetchWorkOrders = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (projectTypeFilter !== 'all') params.project_type = projectTypeFilter;
      if (searchQuery) params.search = searchQuery;
      
      // Crew Leaders only see their assigned work orders
      // Supervisors and Admins see all work orders
      let response;
      if (isCrewLeaderOnly) {
        response = await getMyWorkOrders(statusFilter !== 'all' ? statusFilter : null);
      } else {
        response = await getWorkOrders(params);
      }
      setWorkOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch work orders:', error);
      toast.error('Failed to load work orders');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchWorkOrders();
  };

  const handleExportCsv = async () => {
    try {
      toast.info('Generating CSV export...');
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      
      const response = await downloadWorkOrdersCsv(params);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `WorkOrders_Export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('CSV export downloaded');
    } catch (error) {
      toast.error('Failed to export CSV');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-amber-100 text-amber-800 border-amber-300',
      in_progress: 'bg-blue-100 text-blue-800 border-blue-300',
      completed: 'bg-green-100 text-green-800 border-green-300',
      closed: 'bg-slate-100 text-slate-800 border-slate-300'
    };
    return colors[status] || colors.pending;
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="work-orders-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {isCrewLeaderOnly ? 'My Work Orders' : 'Work Orders'}
          </h1>
          <p className="text-slate-600">
            {isCrewLeaderOnly 
              ? 'Work orders assigned to you' 
              : 'Manage and track all substation projects'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportCsv} data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          {isSupervisor() && (
            <Link to="/work-orders/new">
              <Button className="bg-[#0056D2] hover:bg-[#0044A4]" data-testid="create-work-order-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Work Order
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by WO#, title, or location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-40" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={projectTypeFilter} onValueChange={setProjectTypeFilter}>
              <SelectTrigger className="w-full md:w-48" data-testid="type-filter">
                <SelectValue placeholder="Project Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="Equipment Replacement">Equipment Replacement</SelectItem>
                <SelectItem value="Testing">Testing</SelectItem>
                <SelectItem value="Emergency">Emergency</SelectItem>
                <SelectItem value="Maintenance">Maintenance</SelectItem>
                <SelectItem value="New Installation">New Installation</SelectItem>
              </SelectContent>
            </Select>
            <Button type="submit" variant="outline" data-testid="search-btn">
              <Filter className="w-4 h-4 mr-2" />
              Apply
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Work Orders Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-[#0056D2]" />
        </div>
      ) : workOrders.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <ClipboardList className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-semibold mb-2">No Work Orders Found</h3>
            <p className="text-slate-500 mb-4">
              {searchQuery || statusFilter !== 'all' || projectTypeFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'Create your first work order to get started'}
            </p>
            {isSupervisor() && (
              <Link to="/work-orders/new">
                <Button className="bg-[#0056D2] hover:bg-[#0044A4]">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Work Order
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {workOrders.map((wo) => (
            <Link key={wo.id} to={`/work-orders/${wo.id}`}>
              <Card 
                className="h-full hover:border-[#0056D2] hover:shadow-md transition-all cursor-pointer"
                data-testid={`work-order-card-${wo.id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-sm text-[#0056D2] font-medium">
                      {wo.wo_number}
                    </span>
                    <Badge className={getStatusColor(wo.status)}>
                      {wo.status?.replace('_', ' ')}
                    </Badge>
                  </div>
                  <CardTitle className="text-lg line-clamp-2">{wo.title}</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-2 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{wo.location_name}</span>
                    </div>
                    {wo.start_date && (
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 flex-shrink-0" />
                        <span>{wo.start_date} - {wo.end_date || 'TBD'}</span>
                      </div>
                    )}
                    {wo.project_type && (
                      <Badge variant="outline" className="mt-2">
                        {wo.project_type}
                      </Badge>
                    )}
                  </div>
                  {wo.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-3">
                      {wo.tags.slice(0, 3).map((tag, i) => (
                        <span 
                          key={i}
                          className="px-2 py-0.5 bg-slate-100 rounded text-xs text-slate-600"
                        >
                          {tag}
                        </span>
                      ))}
                      {wo.tags.length > 3 && (
                        <span className="px-2 py-0.5 text-xs text-slate-400">
                          +{wo.tags.length - 3} more
                        </span>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default WorkOrdersPage;
